import {
	Button,
	Grid,
	IconButton,
	InputAdornment,
	TextField,
	Theme,
	Typography,
	makeStyles,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import {
	ColDef,
	GetContextMenuItemsParams,
	GridApi,
	MenuItemDef,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { useContextMenuMESF } from "../../../../../controls";
import { LazyLoading } from "../../../../../controls/LazyLoading/LazyLoading";
import { ChangePassword } from "../ChangePassword/ChangePassword";
import { CreateUser } from "../Create/CreateUser";
import { DeleteUser } from "../Delete/DeleteUser";
import { EditUser } from "../EditUser/EditUser";
import { useTableData } from "./customHooks/useTableData";
import { useUsersOptionsFunctions } from "./customHooks/useUsersOptionsFunctions";

const useStyles = makeStyles((theme: Theme) => ({
	root: {
		"& .ag-icon-menu": {
			display: "none",
		},
		"& .ag-header-cell-label": {
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			fontSize: "0.85rem",
		},
		"& .ag-header-cell-text": {
			textAlign: "center !important",
		},

		"& .ag-checkbox-input-wrapper": {
			margin: "0 10px",
		},
		"& .ag-cell": {
			alignItems: "center",
			padding: "0 !important",
			border: "unset !important",
		},
		"& .ag-header-cell": {
			padding: "0px !important",
			textAlign: "center !important",
			fontSize: 10,
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
		},
		"& .ag-cell-wrapper": {
			textAlign: "center",
		},
		"& .ag-menu-option": {
			cursor: "pointer",
		},
		"& .MuiDataGrid-row": {
			cursor: "pointer",
		},
		"& .MuiDataGrid-columnHeaderTitle, & .MuiTablePagination-caption": {
			fontWeight: "bold",
			fontSize: "12px",
		},
		"& .MuiDataGrid-columnHeaderTitleContainer": {
			padding: "0",
		},
		"& .MuiDataGrid-cell--textLeft": {
			fontSize: "12px",
		},
	},
	relative: {
		position: "relative",
	},
	textfield: {
		margin: theme.spacing(2),
	},
	btnModal: {
		display: "flex",
		justifyContent: "flex-end",
	},
	dataTable: {
		padding: theme.spacing(1, 2),
	},
}));

type Props = {
	getUsersFromAPI: Function;
	isLoading: boolean;
};
export const TableUsers = ({ getUsersFromAPI, isLoading }: Props) => {
	const gridStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);
	const [openModalCreate, setOpenModalCreate] = useState(false);
	const [openModalEditDataUser, setOpenModalEditDataUser] = useState(false);
	const [openModalDeleteUser, setOpenModalDeleteUser] = useState(false);
	const [openModalChangePassword, setOpenModalChangePasswordd] =
		useState(false);

	const classes = useStyles();

	const [userId, setUserId] = useState<number | null>(null);
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [filterValue, setFilterValue] = useState("");

	const loadingOverlayComponent = useMemo<any>(() => {
		return LazyLoading;
	}, []);
	const { getMenuOptions } = useUsersOptionsFunctions({
		setUserId,
		setShowEditModal: setOpenModalEditDataUser,
		setShowDeleteModal: setOpenModalDeleteUser,
		setShowCreateModal: setOpenModalCreate,
		setShowChangePasswordModal: setOpenModalChangePasswordd,
	});
	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const { rows, columnDefs } = useTableData({
		setUserId,
		setOpenModalEditDataUser,
		setOpenModalDeleteUser,
		setOpenModalChangePasswordd,
		showContextMenu,
	});
	const defaultColDef = useMemo<ColDef>(() => {
		return {
			sortable: true,
			wrapText: true,
			autoHeight: true,
			wrapHeaderText: true,
		};
	}, []);

	const getContextMenuItems = (
		params: GetContextMenuItemsParams,
	): (string | MenuItemDef)[] => {
		const data = params.node?.data;
		params.api.deselectAll();
		params.node?.setSelected(true);
		showContextMenu(event as any, data, "TableUsers");
		return [];
	};

	useEffect(() => {
		if (isLoading) {
			gridApi?.showLoadingOverlay();
		} else {
			gridApi?.hideOverlay();
		}
	}, [isLoading, rows, gridApi]);

	useEffect(() => {
		registerConfig({
			id: "TableUsers",
			getOptions: getMenuOptions,
		});
	}, []);

	return (
		<Grid container justifyContent="center">
			<Grid item md={12} xs={12} style={{ padding: "0 15px" }}>
				<Typography
					variant="h5"
					style={{ margin: "10px 0 6px", fontWeight: 600 }}
				>
					User Management
				</Typography>
			</Grid>
			<Grid item md={12} xs={12} style={{ padding: "0 15px" }}>
				<TextField
					fullWidth
					label="Search"
					variant="outlined"
					size="small"
					margin="dense"
					value={filterValue}
					onChange={(event) => {
						setFilterValue(event.target.value);
						gridApi?.setQuickFilter(event.target.value);
					}}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<IconButton edge="start" type="submit">
									<SearchIcon />
								</IconButton>
							</InputAdornment>
						),
					}}
				/>
			</Grid>
			<Grid
				container
				justifyContent="center"
				alignItems="center"
				className={classes.root}
			>
				<Grid
					item
					md={12}
					xs={12}
					style={{ padding: "0 15px" }}
					className={classes.relative}
				>
					<CreateUser
						show={openModalCreate}
						onHide={(shouldUpdate: boolean) => {
							setOpenModalCreate(false);
							setUserId(null);
							if (shouldUpdate) getUsersFromAPI();
						}}
					/>
					<EditUser
						show={openModalEditDataUser}
						userId={userId}
						onHide={(shouldUpdate: boolean) => {
							setOpenModalEditDataUser(false);
							setUserId(null);
							if (shouldUpdate) getUsersFromAPI();
						}}
					/>
					<ChangePassword
						show={openModalChangePassword}
						userId={userId}
						onHide={(shouldUpdate) => {
							setOpenModalChangePasswordd(false);
							setUserId(null);
							if (shouldUpdate) {
								getUsersFromAPI();
							}
						}}
					/>
					<DeleteUser
						show={openModalDeleteUser}
						userId={userId}
						onHide={(shouldUpdate: boolean) => {
							setOpenModalDeleteUser(false);
							setUserId(null);
							if (shouldUpdate) getUsersFromAPI();
						}}
					/>
					<Grid
						item
						md={12}
						xs={12}
						style={{
							height: "61vh",
							margin: "6px 0 10px",
						}}
					>
						<div style={gridStyle} className="ag-theme-balham">
							<AgGridReact
								rowData={rows}
								columnDefs={columnDefs}
								defaultColDef={defaultColDef}
								rowHeight={34}
								headerHeight={34}
								loadingOverlayComponent={loadingOverlayComponent}
								animateRows={true}
								getContextMenuItems={getContextMenuItems}
								getRowId={(params) => params.data.UserId}
								statusBar={{
									statusPanels: [
										{ statusPanel: "agTotalRowCountComponent", align: "left" },
										{
											statusPanel: "agFilteredRowCountComponent",
											align: "left",
										},
									],
								}}
								rowSelection="single"
								onRowDoubleClicked={(event) => {
									setUserId(event.data.UserId);
									setOpenModalEditDataUser(true);
								}}
								onGridReady={(params) => setGridApi(params.api)}
							/>
						</div>
					</Grid>
					<Grid container justifyContent="flex-end">
						<Grid item md={2} xs={12} className={classes.btnModal}>
							<Button
								variant="contained"
								color="primary"
								onClick={() => setOpenModalCreate(!openModalCreate)}
								fullWidth
								style={{ marginTop: "10px" }}
							>
								New user
							</Button>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
};
